import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { availabilitiesApiSlice } from "../../../../api/availabilities/availabilitiesApiSlice";
import CloseIcon from "../../../../components/icons/CloseIcon";
import { Button } from "../../../../components/ui/button";
import { Spinner } from "../../../../components/ui/spinner";
import { useApplyCoupon } from "../../../../redux/flows/applyCouponFlow";
import { useCreateNewReservation } from "../../../../redux/flows/createReservationFlow";
import { useFetchGroups } from "../../../../redux/flows/fetchGroupsFlow";
import {
  bookingCreationCompleted as EquipmentRentalBookingCreationCompleted,
  dateSelectedClear,
  durationSelectedClear,
  setAvailabilities,
  setUseCase,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import {
  bookingCreationCompleted as TempStorageBookingCreationCompleted,
  newBookingFlowChanged,
  newBookingStepChanged,
} from "../../../../redux/slice/temporaryStorage/temporaryStorageSlice";
import { RootState, baseStore } from "../../../../redux/store";
import routes from "../../../../routes/routes";
import { usePrevious } from "../../../../util/usePrevious";
import GeneralDialog from "../../../common/GeneralDialog";
import Header from "../../../layout/components/Header";
import TenantLogo from "../../../layout/components/TenantLogo";
import BookLocationHeader from "../../common/util/BookLocationHeader";
import TemStorageNavigateBackAction from "../../common/util/TemStorageNavigateBackAction";
import TemporaryStorageBookingSummaryStep from "../steps/TemporaryStorageBookingSummaryStep";
import TemporaryStorageDateDurationSelectionStep from "../steps/TemporaryStorageDateDurationSelectionStep";
import TemporaryStorageLocationSelectionStep from "../steps/TemporaryStorageLocationSelectionStep";
import TemporaryStorageLockerSizeSelectionStep from "../steps/TemporaryStorageLockerSizeSelectionStep";
import TemporaryStoragePersonalDataStep from "../steps/TemporaryStoragePersonalDataStep";
import Payment from "./Payment";
import useServiceStorage from "../../../../hooks/useServiceStorage";
import { ServiceTypeSelectorOption } from "../../../../types/order";
import { useBookingWindow } from "../../common/hooks/useBookingWindow";
import { loginSuppliesContactData } from "../../../../authentication/loginSuppliesContactData";

export enum TemporaryStorageSteps {
  locationSelection,
  lockerSizeSelection,
  dateDurationSelection,
  personalData,
  bookingSummary,
  payment,
}

interface BookLocationDialogProps {
  returnPage: string;
}

const TemporaryStorageNewBookingPage = (props: BookLocationDialogProps) => {
  const stepWrapperRef = useRef<HTMLElement>();
  const [pageDialogOpen, setPageDialogOpen] = useState<boolean>(false);
  const [loadingCreatingReservation, setLoadingCreatingReservation] =
    useState(false);
  const dispatch = useDispatch();
  // The configured booking horizon for this use case — replaces the hardcoded
  // `add(1, "months").endOf("month")` this file computed inline. One month is
  // the DEFAULT here, so an unconfigured tenant requests exactly the same range
  // as before. `earliest` carries the lead buffer, which defaults to 0 for this
  // use case (the customer may book now), and is the same rule the app API
  // enforces (ADR-0020, `backend/src/core/lib/bookingWindow.ts`).
  const bookingWindow = useBookingWindow("storage");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const fetchGroups = useFetchGroups("storage");
  const assetGroupId = useSelector(
    (state: RootState) => state.bookingCreation.assetGroupId
  );
  const createNewReservation = useCreateNewReservation();

  const step = useSelector((state: RootState) => state.temporaryStorage.step);
  const newBookingFlow = useSelector((state: RootState) => state.temporaryStorage.newBookingFlow);
  const reservations = useSelector(
    (state: RootState) => state.bookingCreation.reservations
  );
  
  const currentStepName = newBookingFlow[step];
  const applyCoupon = useApplyCoupon();
  const appliedCouponCode = useSelector(
    (state: RootState) => state.bookingCreation.appliedCouponCode
  );
  const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);
  const { updateService, } = useServiceStorage(
    "selectedService",
    ServiceTypeSelectorOption.STORAGE
  );
  const previousStep = usePrevious(step);
  useEffect(() => {
    stepWrapperRef.current = document.getElementById(
      "temporaryStorageProcessStepWrapper"
    )!;

    dispatch(
      newBookingStepChanged({
        step: TemporaryStorageSteps.locationSelection,
        nextEnabled: true,
      })
    );

    // Set the usecase
    dispatch(setUseCase({ useCase: "temp_storage_new_booking" }));

    // Set the bookingFlow - exclude personalData step when the login supplies it
    const bookingFlow = loginSuppliesContactData(tenantConfig?.authentication)
      ? [
          "locationSelection",
          "lockerSizeSelection",
          "dateDurationSelection",
          "bookingSummary",
          "payment",
        ]
      : [
          "locationSelection",
          "lockerSizeSelection",
          "dateDurationSelection",
          "personalData",
          "bookingSummary",
          "payment",
        ];
    
    dispatch(
      newBookingFlowChanged({
        newBookingFlow: bookingFlow,
      })
    );
  }, [tenantConfig, dispatch]);

  const headerText = useMemo(() => {
    switch (currentStepName) {
      case "locationSelection":
        return t("temporary_storage.new_booking.location.header_text");
      case "lockerSizeSelection":
        return t("temporary_storage.new_booking.locker_size.header_text");
      case "dateDurationSelection":
        return t("temporary_storage.new_booking.date_duration.header_text");
      case "personalData":
        return t("temporary_storage.new_booking.persons_details.header_text");
      case "bookingSummary":
        return t("temporary_storage.new_booking.booking_summary.header_text");
      default:
        return "";
    }
  }, [currentStepName, t]);

  const fetchAssetGroupAvailabilities = async (
    assetGroupId: string,
    from: Date,
    to: Date
  ) => {
    const data = await baseStore.dispatch(
      availabilitiesApiSlice.endpoints.fetchAssetGroupAvailabilities.initiate({
        assetGroupId: assetGroupId,
        from: from,
        to: to,
      })
    );
    return data;
  };

  useEffect(() => {
    const manageReduxData = async () => {
      if (currentStepName === "lockerSizeSelection") {
        // Clean data
        dispatch(dateSelectedClear());
        dispatch(durationSelectedClear());
        await fetchGroups();
      } else if (
        currentStepName === "dateDurationSelection" &&
        assetGroupId &&
        previousStep !== undefined &&
        previousStep < step
      ) {
        // TODO change to user input
        const fromDate = bookingWindow.earliest.toDate();
        const toDate = bookingWindow.latest.toDate();
        const { data: availabilities } = await fetchAssetGroupAvailabilities(
          assetGroupId,
          fromDate,
          toDate
        );

        if (availabilities) {
          dispatch(setAvailabilities(availabilities.availabilities));
        }
      }
      const previousStepName = newBookingFlow[previousStep ?? -1];
      const isTransitioningToSummary = 
        previousStepName && 
        (previousStepName === "dateDurationSelection" || previousStepName === "personalData") &&
        currentStepName === "bookingSummary";
      
      if (isTransitioningToSummary) {
        if (reservations.length === 0) {
          setLoadingCreatingReservation(true);
          const processedReservation = await createNewReservation();
          updateService(ServiceTypeSelectorOption.STORAGE);


          if (processedReservation && appliedCouponCode) {
            const reservationIds = reservations.map(
              (reservation: any) => reservation.reservationId!
            );
            reservationIds.push(processedReservation.reservationId!);
            await applyCoupon(reservationIds, appliedCouponCode);
          }
          setLoadingCreatingReservation(false);
        }
      }

    };
    manageReduxData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const isLoading = () => {
    if (currentStepName === "locationSelection") {
      return false;
    }

    return false;
  };

  const closePage = () => {
    navigate(routes.home);
    dispatch(TempStorageBookingCreationCompleted());
    dispatch(EquipmentRentalBookingCreationCompleted());
  };

  // useEffect(() => {
  //   if (locations && step === 0) {
  //     const svcLocations = getSvcLocations(locations, 'rental');
  //     if (svcLocations.length === 1) {
  //       dispatch(navigatedToNextStep());
  //       dispatch(setSelectedLocation(svcLocations[0]));
  //       dispatch(setHasSelectedLocation(true));
  //     }
  //   }
  // }, [locations, step, dispatch]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Header>
        <div className="h-12 absolute left-2 flex items-center">
          <TemStorageNavigateBackAction />
        </div>
        <TenantLogo />
        <Button
          id="closeButton"
          variant="ghost"
          size="icon"
          onClick={() => setPageDialogOpen(true)}
          aria-label={t("common.close", { defaultValue: "Close" })}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full text-foreground/90"
        >
          <CloseIcon />
        </Button>
      </Header>

      <section className="flex-1">
        <div className="max-w-[1000px] mx-auto">
          <BookLocationHeader text={headerText} />
          <div className="mt-6">
            {currentStepName === "locationSelection" && (
              <TemporaryStorageLocationSelectionStep />
            )}
            {currentStepName === "lockerSizeSelection" && (
              <TemporaryStorageLockerSizeSelectionStep />
            )}
            {currentStepName === "dateDurationSelection" && (
              <TemporaryStorageDateDurationSelectionStep />
            )}
            {currentStepName === "personalData" && <TemporaryStoragePersonalDataStep />}
            {currentStepName === "bookingSummary" && (
              <TemporaryStorageBookingSummaryStep loading={loadingCreatingReservation} />
            )}
            {currentStepName === "payment" && <Payment />}
          </div>
        </div>
      </section>

      {isLoading() && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Spinner size="lg" />
        </div>
      )}

      <GeneralDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        onContinue={closePage}
        content={t("use_order.closing_dialog.content")}
      />
    </div>
  );
};

export default TemporaryStorageNewBookingPage;